import { useState, useEffect, useRef } from 'react';
import { apiClient, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import AppPageIntro from '@/components/AppPageIntro';
import { FEATURE_KEYS } from '@/lib/saas';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  PiggyBank,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Wifi,
  Shield,
  ChevronRight,
  Sparkles,
  Check,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary' }) => (
  <Card className="card-interactive shadow-card hover:shadow-card-hover">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className={`font-mono text-2xl font-bold ${color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : 'text-foreground'}`}>
            {value}
          </p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color === 'green' ? 'bg-green-100' : color === 'red' ? 'bg-red-100' : 'bg-primary/10'}`}>
          <Icon className={`w-6 h-6 ${color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : 'text-primary'}`} />
        </div>
      </div>
      {trend && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
          {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          <span>{trendValue}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const ContractAlert = ({ contract, onClick }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'energy': return Zap;
      case 'internet': return Wifi;
      case 'insurance': return Shield;
      default: return AlertTriangle;
    }
  };
  const Icon = getIcon(contract.type);

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 rounded-xl border transition-all hover:shadow-md text-left ${
        contract.alert_level === 'urgent' ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          contract.alert_level === 'urgent' ? 'bg-red-100' : 'bg-orange-100'
        }`}>
          <Icon className={`w-5 h-5 ${contract.alert_level === 'urgent' ? 'text-red-600' : 'text-orange-600'}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="font-semibold">{contract.provider}</p>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              contract.alert_level === 'urgent' ? 'bg-red-200 text-red-700' : 'bg-orange-200 text-orange-700'
            }`}>
              {contract.days_until_end} dagen
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Bespaar €{contract.potential_savings}/jaar
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </div>
    </button>
  );
};

export default function Dashboard() {
  const { user, hasPremiumAccess } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [nibudData, setNibudData] = useState([]);
  const [contractAlerts, setContractAlerts] = useState([]);
  const [savingsGoals, setSavingsGoals] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [benefitsSummary, setBenefitsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  });
  const [isMoreInsightsOpen, setIsMoreInsightsOpen] = useState(false);
  const budgetAlertToastShown = useRef(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    const alertCount = Number(budgetSummary?.alerts?.total_alert_categories || 0);
    if (alertCount > 0 && !budgetAlertToastShown.current) {
      toast.error(budgetSummary?.alerts?.headline || `Je zit deze maand in ${alertCount} categorie${alertCount > 1 ? 'en' : ''} niet meer op schema.`);
      budgetAlertToastShown.current = true;
    }
  }, [budgetSummary]);

  useEffect(() => {
    const onResize = () => setIsCompactViewport(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [
        summaryRes,
        budgetSummaryRes,
        nibudRes,
        alertsRes,
        goalsRes,
        reservationsRes,
        transRes,
        benefitsRes,
      ] = await Promise.all([
        apiClient.get('/dashboard/summary').catch(() => ({ data: {} })),
        apiClient.get('/budgets/summary/current').catch(() => ({ data: null })),
        apiClient.get('/nibud/comparison').catch(() => ({ data: [] })),
        apiClient.get('/contracts/alerts').catch(() => ({ data: [] })),
        apiClient.get('/savings/goals').catch(() => ({ data: [] })),
        apiClient.get('/reservations').catch(() => ({ data: [] })),
        apiClient.get('/transactions?limit=5').catch(() => ({ data: [] })),
        apiClient.get('/family/benefits-summary').catch(() => ({ data: null })),
      ]);

      setSummary(summaryRes.data || {});
      setBudgetSummary(budgetSummaryRes.data || null);
      setNibudData(Array.isArray(nibudRes.data) ? nibudRes.data.slice(0, 6) : []);
      setContractAlerts(Array.isArray(alertsRes.data) ? alertsRes.data : []);
      setSavingsGoals(Array.isArray(goalsRes.data) ? goalsRes.data : []);
      setReservations(Array.isArray(reservationsRes.data) ? reservationsRes.data : []);
      setRecentTransactions(Array.isArray(transRes.data) ? transRes.data : []);
      setBenefitsSummary(benefitsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const chartData = nibudData.map((item) => ({
    name: String(item.category || 'Overig').substring(0, 8),
    uitgaven: Number(item.user_amount || 0),
    nibud: Number(item.nibud_norm || 0)
  }));
  const nextKinderbijslagDate = benefitsSummary?.kinderbijslag_next_payment_date
    ? new Date(benefitsSummary.kinderbijslag_next_payment_date).toLocaleDateString('nl-NL')
    : null;
  const showPremiumTeaser = !hasPremiumAccess(FEATURE_KEYS.CONTRACT_ALERTS);
  const onboardingSteps = [
    {
      key: 'accounts',
      title: 'Voeg je eerste bankrekening toe',
      description: 'Start in Instellingen met een rekening, saldo en type.',
      done: Number(summary?.accounts_count || 0) > 0,
      cta: 'Naar Instellingen',
      onClick: () => navigate('/settings'),
    },
    {
      key: 'transactions',
      title: 'Importeer je eerste transacties',
      description: 'Upload een CSV zodat je dashboard en categorieen gevuld raken.',
      done: recentTransactions.length > 0,
      cta: 'Naar Transacties',
      onClick: () => navigate('/transactions'),
    },
    {
      key: 'planning',
      title: 'Maak een eerste reservering of vaste last',
      description: 'Zet geld apart voor wat eraan komt, zodat je beter ziet wat vrij te besteden is.',
      done: reservations.length > 0 || savingsGoals.length > 0 || Number(summary?.monthly_expenses || 0) > 0,
      cta: reservations.length > 0 || savingsGoals.length > 0 ? 'Naar reserveringen' : 'Plan vooruit',
      onClick: () => navigate('/reservations'),
    },
  ];
  const showOnboardingCard = onboardingSteps.some((step) => !step.done);
  const budgetCategories = Array.isArray(budgetSummary?.categories) ? budgetSummary.categories : [];
  const budgetAlertCount = Number(budgetSummary?.alerts?.total_alert_categories || 0);
  const budgetHasAlerts = budgetAlertCount > 0;
  const workIncomeSummary = summary?.work_income_summary || null;
  const expectedIncomeSummary = summary?.expected_income_summary || null;
  const zzpSummary = summary?.zzp_summary || null;
  const earlyWarningSummary = summary?.early_warning_summary || { status: 'ok', headline: '', items: [] };
  const safeToSpendSummary = summary?.safe_to_spend_summary || null;
  const reservationsSummary = summary?.reservations_summary || {
    reserved_total: 0,
    available_after_reservations: Number(summary?.total_balance || 0),
    monthly_reserve_total: 0,
    count: 0,
  };
  const reservationCategoryOptions = [
    { value: 'vacation', label: 'Vakantie' },
    { value: 'car', label: 'Auto-onderhoud' },
    { value: 'insurance', label: 'Verzekering' },
    { value: 'buffer', label: 'Buffer' },
    { value: 'tax', label: 'Belasting' },
    { value: 'other', label: 'Overig' },
  ];
  const reservationFrequencyLabels = {
    monthly: 'per maand',
    quarterly: 'per kwartaal',
    yearly: 'per jaar',
  };
  const accountBalanceBreakdown = Array.isArray(summary?.account_balance_breakdown) ? summary.account_balance_breakdown : [];
  const latestKnownBalanceDate = accountBalanceBreakdown
    .map((account) => account.last_balance_date)
    .filter(Boolean)
    .sort()
    .slice(-1)[0] || null;
  const safeToSpendToneClasses = {
    ok: 'border-green-200 bg-green-50 text-green-700',
    warning: 'border-amber-200 bg-amber-50 text-amber-700',
    risk: 'border-red-200 bg-red-50 text-red-700',
  };
  const earlyWarningToneClasses = {
    ok: 'border-green-200 bg-green-50/70',
    watch: 'border-amber-200 bg-amber-50/70',
    attention: 'border-red-200 bg-red-50/70',
  };
  const getBudgetStatusClasses = (status) => {
    switch (status) {
      case 'over':
        return 'border-red-200 bg-red-50 text-red-700';
      case 'reached':
        return 'border-orange-200 bg-orange-50 text-orange-700';
      case 'warning':
        return 'border-amber-200 bg-amber-50 text-amber-700';
      default:
        return 'border-green-200 bg-green-50 text-green-700';
    }
  };
  const dashboardDetailsOpen = !isCompactViewport || isMoreInsightsOpen;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <AppPageIntro
        eyebrow="Vandaag in budgettoppers"
        title={`Welkom, ${user?.name?.split(' ')[0]}!`}
        description="Hier zie je in een oogopslag je maandruimte, rekeningstanden en vroege signalen. Dit is je app-startpunt voor wat nu aandacht vraagt."
        badges={['Dashboard', 'Maandoverzicht', 'App-overzicht']}
        action={
          summary?.total_potential_savings > 0 ? (
            <div className="inline-flex items-center gap-3 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900">
              <Sparkles className="w-5 h-5 text-amber-700" />
              <span>€{summary.total_potential_savings} besparing mogelijk</span>
            </div>
          ) : null
        }
      />
      {showOnboardingCard && (
        <Card className="shadow-card border-primary/15 bg-primary/5">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Eerste stappen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Zo haal je het snelst waarde uit budgettoppers. Rond deze basisstappen af voor een bruikbaar overzicht.
            </p>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {onboardingSteps.map((step) => (
                <div key={step.key} className="rounded-xl border bg-background p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{step.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{step.description}</p>
                    </div>
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full ${step.done ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      <Check className="w-4 h-4" />
                    </div>
                  </div>
                  <Button
                    variant={step.done ? 'outline' : 'default'}
                    className="mt-4 w-full rounded-full"
                    onClick={step.onClick}
                  >
                    {step.done ? 'Bekijk' : step.cta}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className={`shadow-card ${earlyWarningToneClasses[earlyWarningSummary.status] || earlyWarningToneClasses.ok}`}>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${earlyWarningSummary.status === 'attention' ? 'text-red-600' : earlyWarningSummary.status === 'watch' ? 'text-amber-600' : 'text-green-600'}`} />
            Vroegsignalering
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-700">
            {earlyWarningSummary.headline || 'Geen directe risico’s in je maandbeeld.'}
          </p>
          {Array.isArray(earlyWarningSummary.items) && earlyWarningSummary.items.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-3">
              {earlyWarningSummary.items.map((item) => (
                <div key={item.type} className="rounded-xl border bg-background p-4">
                  <p className="font-medium">{item.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{item.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border bg-background p-4 text-sm text-muted-foreground">
              Je begroting, reserveringen en maandruimte geven nu geen direct signaal dat er iets scheef loopt.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Laatst bekende rekeningstand"
          value={`€${summary?.total_balance?.toLocaleString('nl-NL')}`}
          subtitle={latestKnownBalanceDate
            ? `Niet live · volgens laatste import/koppeling t/m ${new Date(latestKnownBalanceDate).toLocaleDateString('nl-NL')} · ${summary?.accounts_count} rekeningen`
            : `Niet live · volgens laatste import/koppeling · ${summary?.accounts_count} rekeningen`}
          icon={Wallet}
        />
        <StatCard
          title="Inkomsten (deze maand)"
          value={`€${summary?.monthly_income?.toLocaleString('nl-NL')}`}
          subtitle={`${new Date().toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })} · tegemoetkomingen apart: €${summary?.government_support_monthly?.toLocaleString('nl-NL')}`}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Uitgaven (deze maand)"
          value={`€${summary?.monthly_expenses?.toLocaleString('nl-NL')}`}
          icon={TrendingDown}
          color="red"
        />
        <StatCard
          title="Vrij te besteden"
          value={`€${Number(summary?.available_to_spend ?? summary?.total_balance ?? 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subtitle={Number(reservationsSummary?.count || 0) > 0
            ? `€${Number(reservationsSummary?.reserved_total || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gereserveerd`
            : 'Nu nog gelijk aan de laatst bekende rekeningstand, omdat er nog niets is gereserveerd'}
          icon={PiggyBank}
          color={Number(summary?.available_to_spend ?? summary?.total_balance ?? 0) >= 0 ? 'green' : 'red'}
        />
      </div>

      <Collapsible open={dashboardDetailsOpen} onOpenChange={setIsMoreInsightsOpen}>
        {isCompactViewport && (
          <Card className="shadow-card">
            <CardContent className="flex items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium">Meer inzichten</p>
                <p className="text-sm text-muted-foreground">
                  Open alleen als je dieper wilt kijken naar budget, trends en detailoverzichten.
                </p>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="rounded-full">
                  {isMoreInsightsOpen ? 'Inklappen' : 'Open'}
                </Button>
              </CollapsibleTrigger>
            </CardContent>
          </Card>
        )}

        <CollapsibleContent className="space-y-6">

      {accountBalanceBreakdown.length > 0 && (
        <Card className="shadow-card border-primary/15">
          <CardHeader>
            <CardTitle className="font-heading">Waar de rekeningstand op is gebaseerd</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            {accountBalanceBreakdown.map((account) => (
              <div key={account.id} className="rounded-xl border bg-background p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium">{account.account_name || account.bank_name || 'Rekening'}</p>
                    <p className="text-sm text-muted-foreground">{account.bank_name || 'Bank'}</p>
                    {account.iban && (
                      <p className="mt-1 text-xs text-muted-foreground">{account.iban}</p>
                    )}
                  </div>
                  <p className="font-mono text-lg font-semibold">
                    €{Number(account.balance || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Gebruikte bron: {account.balance_source === 'transaction' ? 'laatste transactie met saldo' : 'rekeningprofiel'}
                  {' · '}
                  Stand per {account.last_balance_date ? new Date(account.last_balance_date).toLocaleDateString('nl-NL') : 'onbekend'}
                </p>
                {account.balance_source === 'transaction' && account.account_balance != null && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Rekeningprofiel stond nog op €{Number(account.account_balance || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {account.account_balance_date ? ` (${new Date(account.account_balance_date).toLocaleDateString('nl-NL')})` : ''}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className={`shadow-card ${budgetHasAlerts ? 'border-red-200 bg-red-50/70' : 'border-green-200 bg-green-50/70'}`}>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${budgetHasAlerts ? 'text-red-600' : 'text-green-600'}`} />
            Begrotingssignaal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {budgetHasAlerts ? (
            <>
              <p className="text-sm text-slate-700">
                {budgetSummary?.alerts?.headline}{' '}
                {Number(budgetSummary?.alerts?.overrun_total || 0) > 0 && (
                  <>Totaal erboven: <span className="font-semibold text-red-600">€{Number(budgetSummary?.alerts?.overrun_total || 0).toFixed(2)}</span>.</>
                )}
              </p>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {budgetCategories.map((item) => (
                  <div key={item.category} className="rounded-xl border bg-background p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          €{Number(item.spent || 0).toFixed(2)} van €{Number(item.budget || 0).toFixed(2)}
                        </p>
                      </div>
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${getBudgetStatusClasses(item.status)}`}>
                        {item.status_label}
                      </span>
                    </div>
                    <div className="mt-3">
                      <Progress value={Math.min(Number(item.percentage || 0), 100)} className="h-2" />
                      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                        <span>{Number(item.percentage || 0).toFixed(0)}%</span>
                        <span>
                          {Number(item.remaining || 0) >= 0
                            ? `Nog €${Number(item.remaining || 0).toFixed(2)} over`
                            : `€${Math.abs(Number(item.remaining || 0)).toFixed(2)} erboven`}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-green-700">
                {budgetSummary?.alerts?.headline || 'Je zit deze maand binnen je begroting. Dat ziet er rustig uit.'}
              </p>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {budgetCategories.map((item) => (
                  <div key={item.category} className="rounded-xl border bg-background p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          €{Number(item.spent || 0).toFixed(2)} van €{Number(item.budget || 0).toFixed(2)}
                        </p>
                      </div>
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${getBudgetStatusClasses(item.status)}`}>
                        {item.status_label}
                      </span>
                    </div>
                    <div className="mt-3">
                      <Progress value={Math.min(Number(item.percentage || 0), 100)} className="h-2" />
                      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                        <span>{Number(item.percentage || 0).toFixed(0)}%</span>
                        <span>Nog €{Number(item.remaining || 0).toFixed(2)} over</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {safeToSpendSummary && (
        <Card className="shadow-card border-primary/15">
          <CardHeader>
            <CardTitle className="font-heading">Vandaag veilig te besteden</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs text-muted-foreground">Vandaag</p>
                <p className="mt-1 font-mono text-2xl font-semibold text-green-600">
                  €{Number(safeToSpendSummary.safe_to_spend_today || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs text-muted-foreground">Nog vrij deze maand</p>
                <p className={`mt-1 font-mono text-2xl font-semibold ${Number(safeToSpendSummary.available_now || 0) >= 0 ? 'text-foreground' : 'text-red-600'}`}>
                  €{Number(safeToSpendSummary.available_now || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs text-muted-foreground">Dagen over</p>
                <p className="mt-1 font-mono text-2xl font-semibold">
                  {Number(safeToSpendSummary.days_left_in_month || 0)}
                </p>
              </div>
            </div>

            <div className={`rounded-xl border p-4 ${safeToSpendToneClasses[safeToSpendSummary.status] || safeToSpendToneClasses.ok}`}>
              <p className="text-sm font-medium">{safeToSpendSummary.headline}</p>
              <p className="mt-1 text-sm">
                Op basis van je huidige saldo, reserveringen en de resterende dagen van deze maand.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs text-muted-foreground">Maandruimte na reserveringen</p>
                <p className={`mt-1 font-mono text-xl font-semibold ${Number(safeToSpendSummary.month_buffer || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  €{Number(safeToSpendSummary.month_buffer || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs text-muted-foreground">Gericht op rust</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Gebruik dit bedrag als rustige daggrens. Dan houd je meer overzicht en minder twijfel bij kleine keuzes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-card border-primary/15">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="font-heading">Reserveringen</CardTitle>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate('/reservations')}>
              Beheer
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border bg-background p-4">
              <p className="text-xs text-muted-foreground">Totaal gereserveerd</p>
              <p className="mt-1 font-mono text-xl font-semibold">€{Number(reservationsSummary?.reserved_total || 0).toFixed(2)}</p>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="text-xs text-muted-foreground">Vrij te besteden</p>
              <p className={`mt-1 font-mono text-xl font-semibold ${Number(reservationsSummary?.available_after_reservations || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                €{Number(reservationsSummary?.available_after_reservations || 0).toFixed(2)}
              </p>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="text-xs text-muted-foreground">Maandimpact reserveringen</p>
              <p className="mt-1 font-mono text-xl font-semibold">€{Number(reservationsSummary?.monthly_reserve_total || 0).toFixed(2)}</p>
            </div>
          </div>

          <div className="rounded-xl border bg-muted/20 p-4">
            <p className="text-sm font-medium">Van saldo naar rust</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Zet geld apart voor dingen die eraan komen, zoals vakantie, onderhoud of verzekering. Zo zie je beter wat vandaag echt vrij beschikbaar is.
            </p>
          </div>

          {reservations.length === 0 ? (
            <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
              Nog geen reserveringen. Begin klein met een potje voor vakantie, buffer of een jaarlijkse rekening.
            </div>
          ) : (
            <div className="grid gap-3 xl:grid-cols-2">
              {reservations.slice(0, 3).map((reservation) => (
                <div key={reservation.id} className="space-y-3 rounded-xl border bg-background p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{reservation.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {reservationCategoryOptions.find((option) => option.value === reservation.category)?.label || 'Overig'}
                        {' · '}
                        {reservationFrequencyLabels[reservation.contribution_frequency] || 'per maand'}
                      </p>
                    </div>
                    <span className="rounded-full border bg-muted/40 px-2.5 py-1 text-xs text-muted-foreground">
                      {Math.round(Number(reservation.progress || 0))}% gevuld
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Gereserveerd</span>
                    <span className="font-mono font-semibold">
                      €{Number(reservation.current_amount || 0).toFixed(2)} / €{Number(reservation.target_amount || 0).toFixed(2)}
                    </span>
                  </div>
                  <Progress value={Math.min(Number(reservation.progress || 0), 100)} className="h-2" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Nog €{Number(reservation.remaining || 0).toFixed(2)} nodig</span>
                    <span>Maandimpact €{Number(reservation.monthly_equivalent || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {reservations.length > 3 && (
            <div className="rounded-xl border bg-muted/20 p-4 text-sm text-muted-foreground">
              Je ziet hier de belangrijkste 3 reserveringen. Houd het dashboard rustig en gebruik deze plek vooral als overzicht.
            </div>
          )}
        </CardContent>
      </Card>

      {benefitsSummary && (
        <Card className="shadow-card border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading">Overheidstegemoetkomingen</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Kinderbijslag (kwartaal)</p>
              <p className="font-mono font-semibold">€{benefitsSummary.kinderbijslag_quarterly?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Kindgebonden budget</p>
              <p className="font-mono font-semibold">€{benefitsSummary.kindgebonden_budget_monthly?.toFixed(2)}/mnd</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Overige toeslagen</p>
              <p className="font-mono font-semibold">€{(benefitsSummary.kinderopvangtoeslag_monthly + benefitsSummary.zorgtoeslag_monthly).toFixed(2)}/mnd</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Totaal tegemoetkomingen</p>
              <p className="font-mono text-xl font-bold text-green-600">€{benefitsSummary.government_support_monthly_total?.toFixed(2)}/mnd</p>
            </div>
            {benefitsSummary.kinderbijslag_next_payment_amount > 0 && nextKinderbijslagDate && (
              <div className="md:col-span-4">
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2">
                  Verwachte kinderbijslag: €{benefitsSummary.kinderbijslag_next_payment_amount?.toFixed(2)} rond {nextKinderbijslagDate}.
                </p>
              </div>
            )}
            {benefitsSummary.notes?.length > 0 && (
              <div className="md:col-span-4">
                <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md p-2">
                  {benefitsSummary.notes[0]}
                </p>
              </div>
            )}
            {benefitsSummary.rule_version && (
              <div className="md:col-span-4">
                <p className="text-xs text-muted-foreground">
                  Regelset: {benefitsSummary.rule_version} • Inkomen gebruikt: €{benefitsSummary.household_income_annual_used?.toFixed(2)}/jaar
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {workIncomeSummary && Number(workIncomeSummary.net_salary_monthly || 0) > 0 && (
        <Card className="shadow-card border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading">Werk & Inkomen</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Netto per maand</p>
              <p className="font-mono font-semibold">€{Number(workIncomeSummary.adjusted_monthly_income || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Vakantiegeld</p>
              <p className="font-mono font-semibold">€{Number(workIncomeSummary.holiday_allowance_annual || 0).toFixed(2)}/jaar</p>
              <p className="text-xs text-muted-foreground">± €{Number(workIncomeSummary.holiday_allowance_monthly_reserve || 0).toFixed(2)} p/m</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">13e maand</p>
              <p className="font-mono font-semibold">
                {Number(workIncomeSummary.thirteenth_month_annual || 0) > 0
                  ? `€${Number(workIncomeSummary.thirteenth_month_annual || 0).toFixed(2)}`
                  : 'Niet ingevuld'}
              </p>
              {Number(workIncomeSummary.thirteenth_month_annual || 0) > 0 && (
                <p className="text-xs text-muted-foreground">± €{Number(workIncomeSummary.thirteenth_month_monthly_reserve || 0).toFixed(2)} p/m</p>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Vakantiedagen open</p>
              <p className="font-mono font-semibold">{Number(workIncomeSummary.vacation_days_remaining || 0)} dagen</p>
              <p className="text-xs text-muted-foreground">± €{Number(workIncomeSummary.vacation_days_remaining_value || 0).toFixed(2)} waarde</p>
            </div>
            {(Number(workIncomeSummary.fixed_allowances_monthly || 0) > 0
              || Number(workIncomeSummary.fixed_deductions_monthly || 0) > 0
              || Number(workIncomeSummary.bike_plan_monthly || 0) > 0) && (
              <div className="md:col-span-4 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
                Vaste vergoedingen: €{Number(workIncomeSummary.fixed_allowances_monthly || 0).toFixed(2)} p/m
                {' · '}Vaste inhoudingen: €{Number(workIncomeSummary.fixed_deductions_monthly || 0).toFixed(2)} p/m
                {' · '}Fietsplan: €{Number(workIncomeSummary.bike_plan_monthly || 0).toFixed(2)} p/m
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {expectedIncomeSummary && Number(expectedIncomeSummary.expected_this_month || 0) > 0 && (
        <Card className="shadow-card border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading">Verwachte inkomsten</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Verwacht deze maand</p>
              <p className="font-mono font-semibold">€{Number(expectedIncomeSummary.expected_this_month || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Al ontvangen</p>
              <p className="font-mono font-semibold">€{Number(expectedIncomeSummary.received_this_month || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Nog verwacht</p>
              <p className="font-mono font-semibold">€{Number(expectedIncomeSummary.remaining_expected_this_month || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Verschil deze maand</p>
              <p className={`font-mono font-semibold ${Number(expectedIncomeSummary.difference_this_month || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                €{Number(expectedIncomeSummary.difference_this_month || 0).toFixed(2)}
              </p>
            </div>
            <div className="md:col-span-4 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              Soort inkomen: {expectedIncomeSummary.income_type === 'salary' ? 'loon' : expectedIncomeSummary.income_type === 'benefit' ? 'uitkering' : expectedIncomeSummary.income_type === 'pension' ? 'pensioen' : expectedIncomeSummary.income_type === 'freelance' ? 'zzp / freelance' : 'overig'}
              {' · '}Ritme: {expectedIncomeSummary.payment_frequency}
              {' · '}Gewone betaaldag: {expectedIncomeSummary.expected_payment_day}
              {' · '}Gematchte inkomsten: {expectedIncomeSummary.matched_transactions_count}
            </div>
          </CardContent>
        </Card>
      )}

      {(expectedIncomeSummary?.income_type === 'freelance' || Number(zzpSummary?.received_income_monthly || 0) > 0) && (
        <Card className="shadow-card border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading">ZZP overzicht</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Ontvangen deze maand</p>
              <p className="font-mono font-semibold">€{Number(zzpSummary?.received_income_monthly || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Belasting apart</p>
              <p className="font-mono font-semibold">€{Number(zzpSummary?.tax_reserve_amount || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Btw apart</p>
              <p className="font-mono font-semibold">€{Number(zzpSummary?.vat_reserve_amount || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Buffer apart</p>
              <p className="font-mono font-semibold">€{Number(zzpSummary?.buffer_reserve_amount || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Veilig besteedbaar</p>
              <p className="font-mono font-semibold text-green-600">€{Number(zzpSummary?.safe_to_spend_amount || 0).toFixed(2)}</p>
            </div>
            <div className="md:col-span-5 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              Gemiddeld ontvangen in de laatste 3 maanden: €{Number(zzpSummary?.average_income_last_3_months || 0).toFixed(2)}
              {' · '}Reserves: {Number(zzpSummary?.tax_reserve_percent || 0).toFixed(1)}% belasting
              {' · '}{Number(zzpSummary?.vat_reserve_percent || 0).toFixed(1)}% btw
              {' · '}{Number(zzpSummary?.buffer_reserve_percent || 0).toFixed(1)}% buffer
              {' · '}Gematchte inkomsten: {Number(zzpSummary?.matched_transactions_count || 0)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Nibud Comparison Chart */}
        <Card className="lg:col-span-2 shadow-card">
          <CardHeader>
            <CardTitle className="font-heading">Uitgaven vs Nibud Norm</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" tickFormatter={(v) => `€${v}`} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value) => `€${value.toFixed(2)}`}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e5e5' }}
                  />
                  <Bar dataKey="uitgaven" name="Jouw uitgaven" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.uitgaven > entry.nibud ? 'hsl(0 70% 55%)' : 'hsl(150 60% 45%)'}
                      />
                    ))}
                  </Bar>
                  <Bar dataKey="nibud" name="Nibud norm" fill="hsl(var(--muted-foreground))" radius={[0, 4, 4, 0]} opacity={0.3} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <Button
              variant="ghost"
              className="mt-4 w-full"
              onClick={() => navigate('/transactions')}
            >
              Bekijk alle transacties
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Contract Alerts */}
        <div className="space-y-6">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading">Contract Alerts</CardTitle>
                {contractAlerts.length > 0 && (
                  <span className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center badge-alert">
                    {contractAlerts.length}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {contractAlerts.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Geen actieve alerts</p>
              ) : (
                <>
                  {contractAlerts.slice(0, 3).map((alert) => (
                    <ContractAlert
                      key={alert.contract_id}
                      contract={alert}
                      onClick={() => navigate('/contracts')}
                    />
                  ))}
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => navigate('/contracts')}
                  >
                    Bekijk alle contracten
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {showPremiumTeaser && (
            <Card className="border-primary/20 bg-primary/5 shadow-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Premium bespaar-alerts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Ontvang automatisch extra seintjes bij contractkansen en krijg sneller zicht op waar je geld kunt besparen.
                </p>
                <Button
                  className="w-full rounded-full bg-primary hover:bg-primary/90"
                  onClick={() => navigate('/settings')}
                >
                  Bekijk Premium
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Savings Goals */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="font-heading">Spaardoelen</CardTitle>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate('/reservations')}>
                Beheer
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {savingsGoals.slice(0, 3).map((goal) => {
              const progress = Number(
                goal.progress ?? (goal.target_amount ? (goal.current_amount / goal.target_amount) * 100 : 0),
              );
              const statusLabel =
                goal.status === 'completed' ? 'Behaald' : goal.status === 'behind' ? 'Achter' : 'Op schema';
              const statusClass =
                goal.status === 'completed'
                  ? 'bg-primary/10 text-primary border border-primary/20'
                  : goal.status === 'behind'
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-emerald-50 text-emerald-700 border border-emerald-200';
              return (
                <div key={goal.id} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{goal.name}</span>
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium ${statusClass}`}>
                      {statusLabel}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      €{Number(goal.current_amount || 0).toLocaleString('nl-NL')} / €{Number(goal.target_amount || 0).toLocaleString('nl-NL')}
                    </span>
                  </div>
                  <Progress value={progress} className="h-2" />
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      Nu €{Number(goal.monthly_contribution || 0).toFixed(2)}/maand • Nodig €{Number(goal.required_monthly_amount || 0).toFixed(2)}/maand
                    </p>
                    <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {goal.target_date
                      ? `Doel op ${new Date(goal.target_date).toLocaleDateString('nl-NL')}`
                      : `${Number(goal.months_remaining || 0)} maanden resterend`}
                    {' · '}
                    Nog €{Number(goal.remaining_amount || 0).toFixed(2)}
                  </p>
                </div>
              );
            })}
            {savingsGoals.length > 3 && (
              <div className="rounded-xl border bg-muted/20 p-4 text-sm text-muted-foreground">
                Je ziet hier de belangrijkste 3 spaardoelen. Zo blijft je dashboard snel scanbaar.
              </div>
            )}
            {savingsGoals.length === 0 && (
              <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
                Nog geen spaardoelen. Voeg er een toe zodra je hier ook vooruit wilt plannen.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="font-heading">Recente Transacties</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentTransactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tx.is_income ? 'bg-green-100' : 'bg-red-100'}`}>
                      {tx.is_income ? (
                        <ArrowUpRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ArrowDownRight className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{tx.merchant || tx.category}</p>
                      <p className="text-xs text-muted-foreground">{tx.category}</p>
                    </div>
                  </div>
                  <span className={`font-mono font-semibold ${tx.is_income ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.is_income ? '+' : ''}€{Math.abs(tx.amount).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            <Button
              variant="ghost"
              className="mt-4 w-full"
              onClick={() => navigate('/transactions')}
            >
              Alle transacties
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
